"use client";

import { useEffect, useState } from "react";
import { Bug, ExternalLink, Loader2, MessageCircleQuestion, X } from "lucide-react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import posthog from "posthog-js";
import { useAuth } from "@/components/auth/AuthProvider";
import { DISCORD_URL } from "@/components/landing/homepage-data";
import { cn } from "@/lib/utils";

type Props = {
  open: boolean;
  onClose: () => void;
  /** Path the user was on when they opened the dialog. Sent with the report. */
  contextPath?: string;
};

type SubmitState = "idle" | "submitting" | "done";
type Kind = "bug" | "idea" | "question";

const KIND_LABELS: Record<Kind, string> = {
  bug: "Bug",
  idea: "Idea",
  question: "Question",
};

export function WrenchIssueDialog({ open, onClose, contextPath }: Props) {
  const { user } = useAuth();
  const isLoggedIn = Boolean(user);
  const [kind, setKind] = useState<Kind>("bug");
  const [message, setMessage] = useState("");
  const [email, setEmail] = useState("");
  const [submitState, setSubmitState] = useState<SubmitState>("idle");

  useEffect(() => {
    if (!open) {
      setKind("bug");
      setMessage("");
      setEmail("");
      setSubmitState("idle");
    }
  }, [open]);

  const submit = async () => {
    if (submitState === "submitting") return;
    if (message.trim().length < 5) {
      toast.error("Please describe the issue in a bit more detail.");
      return;
    }
    setSubmitState("submitting");
    let sessionId: string | null = null;
    let distinctId: string | null = null;
    try {
      sessionId = posthog.get_session_id?.() ?? null;
      distinctId = posthog.get_distinct_id?.() ?? null;
    } catch {
      // posthog uninitialized — fall through with nulls
    }
    try {
      const res = await fetch("/api/feedback", {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({
          kind,
          message: message.trim(),
          email: email.trim() || null,
          path: contextPath ?? null,
          userAgent: typeof navigator !== "undefined" ? navigator.userAgent : null,
          posthog_session_id: sessionId,
          posthog_distinct_id: distinctId,
        }),
      });
      if (!res.ok) throw new Error(`Server returned ${res.status}`);
      toast.success("Thanks — Wrench passed it along.");
      setSubmitState("done");
      window.setTimeout(() => onClose(), 800);
    } catch (err) {
      setSubmitState("idle");
      toast.error(err instanceof Error ? err.message : "Couldn't send your report.");
    }
  };

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center bg-background/75 px-4 backdrop-blur-md"
      onClick={onClose}
    >
      <div
        className="premium-panel w-full max-w-lg overflow-hidden rounded-[1.5rem]"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between border-b border-border/60 px-5 py-4">
          <div className="flex items-center gap-2.5">
            <span className="grid h-8 w-8 place-items-center rounded-xl border border-accent/30 bg-accent/10 text-accent">
              <Bug className="h-4 w-4" />
            </span>
            <div>
              <p className="font-mono text-[10px] uppercase tracking-[0.22em] text-muted-foreground">
                Send to Wrench
              </p>
              <h2 className="text-sm font-semibold tracking-tight text-foreground">Report an issue</h2>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="text-muted-foreground transition hover:text-foreground"
            aria-label="Close report dialog"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        {!isLoggedIn ? (
          <div className="flex flex-col items-center gap-3 px-5 py-10 text-center">
            <p className="text-sm font-medium text-foreground">Sign in to report an issue</p>
            <p className="text-xs text-muted-foreground">You need an account to submit bug reports.</p>
            <Link
              to="/auth"
              onClick={onClose}
              className="mt-1 rounded-full border border-primary/30 bg-primary px-5 py-2 text-xs font-semibold text-primary-foreground transition hover:bg-primary/90"
            >
              Sign in
            </Link>
          </div>
        ) : null}

        <div className={cn("space-y-4 px-5 py-5", !isLoggedIn && "hidden")}>
          <div className="flex flex-wrap gap-1.5">
            {(Object.keys(KIND_LABELS) as Kind[]).map((k) => (
              <button
                key={k}
                type="button"
                onClick={() => setKind(k)}
                className={cn(
                  "rounded-full border px-3 py-0.5 text-[11px] transition",
                  kind === k
                    ? "border-accent/45 bg-accent/15 text-foreground"
                    : "border-border/70 bg-secondary/40 text-muted-foreground hover:border-accent/30 hover:text-foreground",
                )}
              >
                {KIND_LABELS[k]}
              </button>
            ))}
          </div>

          {kind === "question" ? (
            <div className="flex flex-col items-center gap-3 rounded-2xl border border-accent/25 bg-accent/[0.06] px-5 py-7 text-center">
              <span className="grid h-10 w-10 place-items-center rounded-xl border border-accent/30 bg-accent/10 text-accent">
                <MessageCircleQuestion className="h-5 w-5" />
              </span>
              <div className="space-y-1">
                <p className="text-sm font-semibold text-foreground">Questions are handled on Discord</p>
                <p className="text-xs leading-relaxed text-muted-foreground">
                  For questions and account help, open a ticket in our Discord — you&apos;ll get a faster,
                  back-and-forth answer from the team there.
                </p>
              </div>
              <a
                href={DISCORD_URL}
                target="_blank"
                rel="noopener noreferrer"
                onClick={onClose}
                className="mt-1 inline-flex items-center gap-1.5 rounded-full border border-primary/30 bg-primary px-4 py-2 text-xs font-semibold text-primary-foreground shadow-[0_18px_50px_hsl(var(--primary)/0.22)] transition hover:-translate-y-0.5 hover:bg-primary/90"
              >
                Open a ticket on Discord
                <ExternalLink className="h-3.5 w-3.5" />
              </a>
            </div>
          ) : (
            <>
              <label className="block">
                <span className="mb-1.5 block font-mono text-[10px] uppercase tracking-[0.22em] text-muted-foreground">
                  What happened?
                </span>
                <textarea
                  autoFocus
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  rows={5}
                  placeholder={
                    kind === "bug"
                      ? "Steps to reproduce, what you expected, what happened instead..."
                      : "Describe what would help and where you'd want it..."
                  }
                  className="w-full resize-none rounded-2xl border border-border/70 bg-secondary/40 px-3.5 py-2.5 text-sm leading-6 text-foreground outline-none placeholder:text-muted-foreground/70 focus:border-accent/45 focus:bg-card/80"
                />
              </label>

              <label className="block">
                <span className="mb-1.5 block font-mono text-[10px] uppercase tracking-[0.22em] text-muted-foreground">
                  Email (optional, for replies)
                </span>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@example.com"
                  className="w-full rounded-2xl border border-border/70 bg-secondary/40 px-3.5 py-2.5 text-sm text-foreground outline-none placeholder:text-muted-foreground/70 focus:border-accent/45 focus:bg-card/80"
                />
              </label>

              {contextPath ? (
                <p className="text-[10px] text-muted-foreground">
                  Sending from <span className="font-mono text-foreground/80">{contextPath}</span>
                </p>
              ) : null}
            </>
          )}
        </div>

        {isLoggedIn ? <div className="flex items-center justify-end gap-2 border-t border-border/60 bg-secondary/30 px-5 py-3">
          <button
            type="button"
            onClick={onClose}
            disabled={submitState === "submitting"}
            className="rounded-full border border-border/70 px-3.5 py-1.5 text-xs text-muted-foreground transition hover:border-accent/35 hover:text-foreground disabled:opacity-50"
          >
            {kind === "question" ? "Close" : "Cancel"}
          </button>
          {kind !== "question" ? (
            <button
              type="button"
              onClick={() => void submit()}
              disabled={submitState === "submitting" || message.trim().length === 0}
              className="inline-flex items-center gap-1.5 rounded-full border border-primary/30 bg-primary px-4 py-1.5 text-xs font-semibold text-primary-foreground shadow-[0_18px_50px_hsl(var(--primary)/0.22)] transition hover:-translate-y-0.5 hover:bg-primary/90 hover:shadow-[0_24px_70px_hsl(var(--primary)/0.28)] disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:translate-y-0"
            >
              {submitState === "submitting" ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : null}
              {submitState === "done" ? "Sent" : "Send to Wrench"}
            </button>
          ) : null}
        </div> : null}
      </div>
    </div>
  );
}
